import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'automationTemplate/<id>';

/**
 * Fetches a single automation template by ID using React Query.
 *
 * This is the foundational query hook for loading automation templates. It provides
 * the raw template data including both draft and published versions of steps and triggers.
 *
 * The query is automatically cached by React Query with the key pattern:
 * `['automationTemplate/<id>', id, impersonationContext]`
 *
 * Caching strategy:
 * - staleTime: 5 minutes (templates rarely change during editing)
 * - gcTime: 10 minutes (keeps data for quick navigation)
 * - refetchOnWindowFocus: false (prevents disruption during editing)
 *
 * Template structure:
 * - draftSteps/draftTrigger: Current working version with unpublished changes
 * - publishedSteps/publishedTrigger: Last committed version (what's active)
 * - status: Draft, Published, or Archived
 * - id, name, description: Basic template metadata
 *
 * @param options - Query configuration
 * @param options.id - Automation template ID to fetch
 * @param options.enabled - Whether to enable the query (default: true)
 * @param options.impersonationContext - Optional impersonation context for admin users
 * @returns React Query result with template data and query state
 *
 * @example
 * ```typescript
 * const { data: template, isLoading, error, refetch } = useAutomationTemplateQuery({
 *   id: templateId,
 *   enabled: Boolean(templateId)
 * });
 *
 * if (isLoading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage error={error} />;
 *
 * // Access draft or published steps:
 * const steps = template.draftSteps || template.publishedSteps;
 * ```
 *
 * @example
 * ```typescript
 * // Admin impersonation:
 * const { data } = useAutomationTemplateQuery({
 *   id: templateId,
 *   impersonationContext: {
 *     userId: adminUserId,
 *     orgId: targetOrgId
 *   }
 * });
 * ```
 */
export function useAutomationTemplateQuery({
	id,
	enabled = true,
	impersonationContext,
}: {
	id: string;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: () => {
			return userSession.webServiceHelper.callAsync<Api.IAutomationTemplateReference>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `AutomationTemplate/${encodeURIComponent(id)}`,
					impersonationContext,
				}),
				'GET'
			);
		},
		queryKey: [BASE_QUERY_KEY, id, impersonationContext],
		// Automation templates rarely change during editing sessions, so we can cache them for 5 minutes.
		// This reduces server load while still ensuring data freshness within a reasonable window.
		staleTime: 5 * 60 * 1000, // 5 minutes
		// Keep the template in cache for 10 minutes after it is no longer in use.
		// This prevents refetching if user navigates away and quickly returns to the same template.
		gcTime: 10 * 60 * 1000, // 10 minutes
		// Don't refetch on window focus - template data is unlikely to change while user is working on it.
		refetchOnWindowFocus: false,
	});
}

/**
 * Invalidates all cached automation template queries, forcing a refetch.
 *
 * This utility function should be called after mutations that affect automation
 * templates to ensure the UI shows the latest data. It invalidates all queries
 * with the 'automationTemplate/<id>' base key.
 *
 * Common use cases:
 * - After publishing a draft (changes status and published fields)
 * - After creating a new template
 * - After deleting a template
 * - After renaming a template
 *
 * @example
 * ```typescript
 * const publishMutation = useCommitAutomationDraftMutation({
 *   onSuccess: () => {
 *     invalidateAllAutomationTemplateQueries();
 *     showToast('Automation published!');
 *   }
 * });
 * ```
 */
export function invalidateAllAutomationTemplateQueries() {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
}
